import React from "react";
import { useSelector, useDispatch } from "react-redux";
import { setModal, closeModalWithUpdate } from "../../../features/modal/ModalSlice";
import apiClient from "../../../services/apiClient";
import toast from "react-hot-toast";

// Import all modal components
import AssistantModal from "./AssistantModal";
import CustomVoiceModal from "./CustomVoiceModal";
import CustomToolModal from "./CustomToolModal";
import ToolModal from "./ToolModal";
import PhoneNumberModal from "./PhoneNumberModal";
import ApiKeyModal from "./ApiKeyModal";
import WorkflowModal from "./WorkflowModal";
import OutboundCampaignModal from "./OutboundCampaignModal";
import EditCampaignModal from "./EditCampaignModal";
import UploadFileModal from "./UploadFileModal";
import IntegrationConfigModal from "./IntegrationConfigModal";
import WidgetModal from "./WidgetModal";
import ViewFileDetailsModal from "./ViewFileDetailsModal";
import ViewCallLogDetailsModal from "./ViewCallLogDetailsModal";
import ViewCallDetailsModal from "./ViewCallDetailsModal";
import ViewApiKeyDetailsModal from "./ViewApiKeyDetailsModal";
import ViewErrorLogDetailsModal from "./ViewErrorLogDetailsModal";


/**
 * ModalManager - Centralized modal management component for VAPI modals
 *
 * This component listens to the Redux modal state and renders the appropriate modal
 * based on the modal type. It handles all VAPI-related modals in one place.
 *
 * Usage:
 * 1. Import this component in App.jsx or a parent component
 * 2. Dispatch modal actions from anywhere: dispatch(setModal({ type: "CREATE_ASSISTANT", data: {...} }))
 * 3. The ModalManager will automatically render the correct modal
 *
 * Modal Types:
 * - CREATE_ASSISTANT / EDIT_ASSISTANT
 * - ADD_CUSTOM_VOICE / EDIT_CUSTOM_VOICE
 * - CREATE_CUSTOM_TOOL / EDIT_CUSTOM_TOOL
 * - ADD_TOOL / EDIT_TOOL (for API-based tools)
 * - ADD_PHONE_NUMBER / EDIT_PHONE_NUMBER
 * - CREATE_API_KEY / EDIT_API_KEY
 * - CREATE_WORKFLOW / EDIT_WORKFLOW
 * - CREATE_OUTBOUND_CAMPAIGN / EDIT_OUTBOUND_CAMPAIGN
 * - UPLOAD_FILE
 * - CONFIGURE_INTEGRATION
 * - CREATE_WIDGET / EDIT_WIDGET
 * - VIEW_FILE_DETAILS
 * - VIEW_WORKFLOW_ANALYTICS
 */
const ModalManager = () => {
  const dispatch = useDispatch();
  const modal = useSelector((state) => state.modal?.modal);
  const userInfo = useSelector((state) => state?.userInfo?.user);

  const handleClose = () => {
    dispatch(setModal(null));
  };

  const handleSave = (data) => {
    // Determine which component to update based on modal type
    const updateTargetMap = {
      CREATE_ASSISTANT: "ASSISTANTS",
      EDIT_ASSISTANT: "ASSISTANTS",
      ADD_CUSTOM_VOICE: "VOICES",
      EDIT_CUSTOM_VOICE: "VOICES",
      CREATE_CUSTOM_TOOL: "TOOLS",
      EDIT_CUSTOM_TOOL: "TOOLS",
      ADD_PHONE_NUMBER: "PHONE_NUMBERS",
      EDIT_PHONE_NUMBER: "PHONE_NUMBERS",
      CREATE_API_KEY: "API_KEYS",
      EDIT_API_KEY: "API_KEYS",
      CREATE_WORKFLOW: "WORKFLOWS",
      EDIT_WORKFLOW: "WORKFLOWS",
      CREATE_OUTBOUND_CAMPAIGN: "OUTBOUND_CALLS",
      EDIT_OUTBOUND_CAMPAIGN: "OUTBOUND_CALLS",
      UPLOAD_FILE: "FILES",
      CONFIGURE_INTEGRATION: "INTEGRATIONS",
      CREATE_WIDGET: "WIDGETS",
      EDIT_WIDGET: "WIDGETS",
    };

    // Close the modal and set update flag to trigger refresh in the component
    dispatch(closeModalWithUpdate({
      isUpdated: {
        isUpdatedIn: updateTargetMap[modal?.type] || modal?.updateTarget,
        data: data,
      },
    }));
  };

  // Handle file upload specifically
  const handleFileUpload = async (modalData) => {
    if (!userInfo?.id) {
      toast.error("User information not found");
      return;
    }

    const uploadedFiles = modalData.files.filter((f) => f.status === "uploaded");
    if (uploadedFiles.length === 0) {
      toast.error("No files to upload");
      return;
    }

    try {
      // Upload each file
      for (const fileData of uploadedFiles) {
        const formData = new FormData();
        formData.append("files", fileData.file);
        formData.append("uploaded_by", userInfo.id);

        await apiClient.post("/knowledge-base/upload", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
      }

      toast.success(`${uploadedFiles.length} file(s) uploaded successfully`);

      // Close modal and trigger refresh
      dispatch(closeModalWithUpdate({
        isUpdated: {
          isUpdatedIn: "FILES",
          data: modalData,
        },
      }));
    } catch (error) {
      console.error("Error uploading files:", error);
      toast.error(error?.response?.data?.message || "Failed to upload files");
    }
  };

  if (!modal) return null;

  const { type, data } = modal;

  // Assistant Modals
  if (type === "CREATE_ASSISTANT" || type === "EDIT_ASSISTANT") {
    return (
      <AssistantModal
        open={true}
        onClose={handleClose}
        data={type === "EDIT_ASSISTANT" ? data : null}
        onSave={handleSave}
      />
    );
  }

  // Custom Voice Modals
  if (type === "ADD_CUSTOM_VOICE" || type === "EDIT_CUSTOM_VOICE") {
    return (
      <CustomVoiceModal
        open={true}
        onClose={handleClose}
        data={type === "EDIT_CUSTOM_VOICE" ? data : null}
        onSave={handleSave}
      />
    );
  }

  // Custom Tool Modals
  if (type === "CREATE_CUSTOM_TOOL" || type === "EDIT_CUSTOM_TOOL") {
    return (
      <CustomToolModal
        open={true}
        onClose={handleClose}
        data={type === "EDIT_CUSTOM_TOOL" ? data : null}
        onSave={handleSave}
      />
    );
  }

  // Phone Number Modals
  if (type === "ADD_PHONE_NUMBER" || type === "EDIT_PHONE_NUMBER") {
    return (
      <PhoneNumberModal
        open={true}
        onClose={handleClose}
        data={type === "EDIT_PHONE_NUMBER" ? data : null}
        onSave={handleSave}
      />
    );
  }

  // API Key Modals
  if (type === "CREATE_API_KEY" || type === "EDIT_API_KEY") {
    return (
      <ApiKeyModal
        open={true}
        onClose={handleClose}
        data={type === "EDIT_API_KEY" ? data : null}
        onSave={handleSave}
      />
    );
  }

  // Workflow Modals
  if (type === "CREATE_WORKFLOW" || type === "EDIT_WORKFLOW") {
    return (
      <WorkflowModal
        open={true}
        onClose={handleClose}
        data={type === "EDIT_WORKFLOW" ? data : null}
        onSave={handleSave}
      />
    );
  }

  // Outbound Campaign Modals
  if (type === "CREATE_OUTBOUND_CAMPAIGN") {
    return (
      <OutboundCampaignModal
        open={true}
        onClose={handleClose}
        data={null}
        onSave={handleSave}
      />
    );
  }

  if (type === "EDIT_OUTBOUND_CAMPAIGN") {
    return (
      <EditCampaignModal
        open={true}
        onClose={handleClose}
        data={data}
        onSave={handleSave}
      />
    );
  }

  // Upload File Modal
  if (type === "UPLOAD_FILE") {
    return (
      <UploadFileModal
        open={true}
        onClose={handleClose}
        onSave={handleFileUpload}
      />
    );
  }

  // Integration Config Modal
  if (type === "CONFIGURE_INTEGRATION") {
    return (
      <IntegrationConfigModal
        open={true}
        onClose={handleClose}
        data={data}
        onSave={handleSave}
      />
    );
  }

  // Widget Modals
  if (type === "CREATE_WIDGET" || type === "EDIT_WIDGET") {
    return (
      <WidgetModal
        open={true}
        onClose={handleClose}
        data={type === "EDIT_WIDGET" ? data : null}
        onSave={handleSave}
      />
    );
  }

  // API-based Tool Modals (for Available Tools section)
  if (type === "ADD_TOOL" || type === "EDIT_TOOL") {
    return <ToolModal />;
  }

  // View File Details Modal
  if (type === "VIEW_FILE_DETAILS") {
    return (
      <ViewFileDetailsModal
        open={true}
        onClose={handleClose}
        data={data}
      />
    );
  }

  // View Call Log Details Modal
  if (type === "VIEW_CALL_LOG_DETAILS") {
    return (
      <ViewCallLogDetailsModal
        open={true}
        onClose={handleClose}
        data={data}
      />
    );
  }

  // View Call Details Modal (for Call Analysis)
  if (type === "VIEW_CALL_DETAILS") {
    return (
      <ViewCallDetailsModal
        open={true}
        onClose={handleClose}
        data={data}
      />
    );
  }

  // View API Key Details Modal
  if (type === "VIEW_API_KEY_DETAILS") {
    return (
      <ViewApiKeyDetailsModal
        open={true}
        onClose={handleClose}
        data={data}
      />
    );
  }

  // View Error Log Details Modal
  if (type === "VIEW_ERROR_LOG_DETAILS") {
    return (
      <ViewErrorLogDetailsModal
        open={true}
        onClose={handleClose}
        data={data}
      />
    );
  }

  return null;
};

export default ModalManager;
